import { useEffect, useRef, useState } from "react";

import ApiRoutes from "../config/api.routes";
import { DocumentType, IpfsDocument } from "../components/DigitalSignature/ipfsDocumentTypes";
import useAuthorization from "../contexts/useAuthorization";
import AxiosFactory from "../services/http.service";

// A document of `type` the connected user created but never signed — they closed the tab mid-way —
// so a signing step can pick it up rather than create a duplicate. Only their own: a pending
// document of the same type can also be someone else's, awaiting this wallet's co-signature.
//
// Pass null to skip the lookup. `key` is per account and type: use it as the React key of the
// creator/signer it feeds, so a draft never carries over to another account. `resolved` is true
// once the lookup has answered for the current key; `documentId` is null when there's no draft.
export default function useOwnDraftDocument(type: DocumentType | null) {
  const { user } = useAuthorization();
  const me = (user?.address ?? "").toLowerCase();

  const key = type && me ? `${me}:${type}` : null;
  const [result, setResult] = useState<{ key: string; documentId: number | null } | null>(null);
  // Read inside the effect without re-running it: the pending list is refreshed with the user,
  // but only a new account or type should start a new lookup.
  const pendingRef = useRef(user?.pendingDocuments);
  pendingRef.current = user?.pendingDocuments;

  useEffect(() => {
    if (!key || !type) {
      return;
    }
    let cancelled = false;
    const ids = (pendingRef.current ?? [])
      .filter((doc) => doc?.type === type)
      .map((doc) => doc.id)
      .sort((a, b) => b - a);

    (async () => {
      const { axios } = new AxiosFactory();
      const docs = await Promise.all(
        ids.map((id) =>
          axios
            .get<IpfsDocument>(ApiRoutes.ipfsDocument.get(id))
            .then((res) => res.data)
            // An unreadable candidate is skipped; worst case a fresh document is created.
            .catch(() => null)
        )
      );
      // Newest first, so a repeat visit keeps the latest draft.
      const found = docs.find((doc) => doc?.creatorAddress?.toLowerCase() === me)?.id ?? null;
      if (!cancelled) {
        setResult({ key, documentId: found });
      }
    })();

    return () => {
      cancelled = true;
    };
    // `me` and `type` only change along with `key`.
  }, [key, me, type]);

  const current = key && result?.key === key ? result : null;
  return { key, resolved: !!current, documentId: current?.documentId ?? null };
}
